import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { X, Plus, Search, Upload, PlusCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { TouchPoint } from '@/types/journey';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

// Common app icons using Simple Icons
const COMMON_APPS = [
  { name: "// Social/Communication", slug: "category_header" }, // Category header
  { name: "Discord", slug: "discord" },
  { name: "Facebook", slug: "facebook" },
  { name: "Gmail", slug: "gmail" },
  { name: "Google", slug: "google" },
  { name: "Instagram", slug: "instagram" },
  { name: "LinkedIn", slug: "custom_linkedin", icon: "https://cdn.iconscout.com/icon/free/png-256/free-linkedin-162-498418.png" },
  { name: "Messenger", slug: "messenger" },
  { name: "Microsoft Teams", slug: "custom_teams", icon: "https://cdn.iconscout.com/icon/free/png-256/free-microsoft-teams-1868806-1583113.png" },
  { name: "Outlook", slug: "custom_outlook" },
  { name: "Signal", slug: "signal" },
  { name: "Slack", slug: "slack" },
  { name: "Telegram", slug: "telegram" },
  { name: "TikTok", slug: "tiktok" },
  { name: "WeChat", slug: "wechat" },
  { name: "WhatsApp", slug: "whatsapp" },
  { name: "X (Twitter)", slug: "x" },
  { name: "YouTube", slug: "youtube" },
  { name: "Zoom", slug: "zoom" },
  
  { name: "// Devices & Platforms", slug: "category_header" }, // Category header
  { name: "Android", slug: "android" },
  { name: "Apple", slug: "apple" },
  { name: "Apple Watch", slug: "custom_applewatch", icon: "https://cdn.iconscout.com/icon/premium/png-256-thumb/apple-watch-21-837473.png" },
  { name: "Chrome", slug: "googlechrome" },
  { name: "Edge", slug: "custom_edge", icon: "https://cdn.iconscout.com/icon/free/png-256/free-microsoft-edge-1-722716.png" },
  { name: "Firefox", slug: "firefox" },
  { name: "iPad", slug: "custom_ipad" },
  { name: "iPhone", slug: "custom_iphone", icon: "https://cdn.iconscout.com/icon/free/png-256/free-iphone-43-432070.png" },
  { name: "Linux", slug: "linux" },
  { name: "macOS", slug: "macos" },
  { name: "Safari", slug: "safari" },
  { name: "Samsung", slug: "samsung" },
  { name: "Windows", slug: "custom_windows" },
  
  { name: "// Entertainment & Media", slug: "category_header" }, // Category header
  { name: "Apple Music", slug: "applemusic" },
  { name: "Apple TV", slug: "appletv" },
  { name: "Disney+", slug: "custom_disney" },
  { name: "HBO", slug: "hbo" },
  { name: "Hulu", slug: "custom_hulu" },
  { name: "Netflix", slug: "netflix" },
  { name: "Prime Video", slug: "primevideo" },
  { name: "Shazam", slug: "shazam" },
  { name: "SoundCloud", slug: "soundcloud" },
  { name: "Spotify", slug: "spotify" },
  { name: "Twitch", slug: "twitch" },
  { name: "YouTube Music", slug: "youtubemusic" },

  { name: "// E-commerce & Payments", slug: "category_header" }, // Category header
  { name: "Amazon", slug: "amazon" },
  { name: "Apple Pay", slug: "applepay" },
  { name: "DoorDash", slug: "doordash" },
  { name: "eBay", slug: "ebay" },
  { name: "Google Pay", slug: "googlepay" },
  { name: "Grubhub", slug: "grubhub" },
  { name: "Mastercard", slug: "mastercard" },
  { name: "PayPal", slug: "paypal" },
  { name: "Shopify", slug: "shopify" },
  { name: "Stripe", slug: "stripe" },
  { name: "Uber Eats", slug: "ubereats" },
  { name: "Visa", slug: "visa" },
  
  { name: "// AI & Tools", slug: "category_header" }, // Category header
  { name: "Adobe", slug: "custom_adobe", icon: "https://cdn.iconscout.com/icon/free/png-256/free-adobe-254-761738.png" },
  { name: "ChatGPT", slug: "openai" }, // OpenAI is the parent company
  { name: "Dropbox", slug: "dropbox" },
  { name: "Figma", slug: "figma" },
  { name: "GitHub", slug: "github" },
  { name: "Google Cloud", slug: "googlecloud" },
  { name: "Google Drive", slug: "googledrive" },
  { name: "Google Maps", slug: "googlemaps" },
  { name: "Google Photos", slug: "googlephotos" },
  { name: "Microsoft Office", slug: "custom_msoffice", icon: "https://cdn.iconscout.com/icon/free/png-256/free-microsoft-office-2-761689.png" },
  { name: "Microsoft 365", slug: "custom_microsoft365" },
  { name: "Notion", slug: "notion" },
  { name: "OneDrive", slug: "custom_onedrive", icon: "https://cdn.iconscout.com/icon/free/png-256/free-microsoft-onedrive-1-282651.png" },
  { name: "Photoshop", slug: "custom_photoshop", icon: "https://cdn.iconscout.com/icon/free/png-256/free-adobe-photoshop-2522533-2132721.png" },
  { name: "Trello", slug: "trello" },
  { name: "WordPress", slug: "wordpress" },
  
  { name: "// Others", slug: "category_header" }, // Category header
  { name: "Airbnb", slug: "airbnb" },
  { name: "AWS", slug: "custom_aws", icon: "https://cdn.iconscout.com/icon/free/png-256/free-aws-1869025-1583149.png" },
  { name: "Behance", slug: "behance" },
  { name: "Bing", slug: "custom_bing", icon: "https://cdn.iconscout.com/icon/free/png-256/free-bing-6-722716.png" },
  { name: "Bitbucket", slug: "bitbucket" },
  { name: "Bitcoin", slug: "bitcoin" },
  { name: "Blogger", slug: "blogger" },
  { name: "Bootstrap", slug: "bootstrap" },
  { name: "CodePen", slug: "codepen" },
  { name: "Coursera", slug: "coursera" },
  { name: "CSS3", slug: "css3" },
  { name: "Deliveroo", slug: "deliveroo" },
  { name: "Deno", slug: "deno" },
  { name: "DuckDuckGo", slug: "duckduckgo" },
  { name: "Ethereum", slug: "ethereum" },
  { name: "Font Awesome", slug: "fontawesome" },
  { name: "Fortnite", slug: "fortnite" },
  { name: "Gatsby", slug: "gatsby" },
  { name: "Gimp", slug: "gimp" },
  { name: "Giphy", slug: "giphy" },
  { name: "GitLab", slug: "gitlab" },
  { name: "Godot", slug: "godotengine" },
  { name: "Go", slug: "go" },
  { name: "Google Analytics", slug: "googleanalytics" },
  { name: "Google Play", slug: "googleplay" },
  { name: "Headspace", slug: "headspace" },
  { name: "Heroku", slug: "heroku" },
  { name: "HTML5", slug: "html5" },
  { name: "iCloud", slug: "icloud" },
  { name: "IMDb", slug: "imdb" },
  { name: "IntelliJ IDEA", slug: "intellijidea" },
  { name: "iOS", slug: "ios" },
  { name: "Java", slug: "custom_java", icon: "https://cdn.iconscout.com/icon/free/png-256/free-java-60-1174953.png" },
  { name: "JavaScript", slug: "javascript" },
  { name: "Jenkins", slug: "jenkins" },
  { name: "Jira", slug: "jira" },
  { name: "jQuery", slug: "jquery" },
  { name: "Kotlin", slug: "kotlin" },
  { name: "Kubernetes", slug: "kubernetes" },
  { name: "Lighthouse", slug: "lighthouse" },
  { name: "Lyft", slug: "lyft" },
  { name: "Markdown", slug: "markdown" },
  { name: "Material Design", slug: "materialdesign" },
  { name: "Medium", slug: "medium" },
  { name: "MongoDB", slug: "mongodb" },
  { name: "Mozilla", slug: "mozilla" },
  { name: "MySQL", slug: "mysql" },
  { name: "Node.js", slug: "nodedotjs" },
  { name: "npm", slug: "npm" },
  { name: "Nuxt.js", slug: "custom_nuxtjs", icon: "https://cdn.iconscout.com/icon/free/png-256/free-nuxt-dot-js-3521615-2945059.png" },
  { name: "OpenAI", slug: "openai" },
  { name: "Opera", slug: "opera" },
  { name: "Patreon", slug: "patreon" },
  { name: "PHP", slug: "php" },
  { name: "Pinterest", slug: "pinterest" },
  { name: "PlayStation", slug: "playstation" },
  { name: "Pocket", slug: "pocket" },
  { name: "PostgreSQL", slug: "postgresql" },
  { name: "PowerPoint", slug: "custom_powerpoint" },
  { name: "Product Hunt", slug: "producthunt" },
  { name: "Python", slug: "python" },
  { name: "Quora", slug: "quora" },
  { name: "React", slug: "react" },
  { name: "Reddit", slug: "reddit" },
  { name: "Redux", slug: "redux" },
  { name: "Salesforce", slug: "salesforce" },
  { name: "Sass", slug: "sass" },
  { name: "Sketch", slug: "sketch" },
  { name: "Snapchat", slug: "snapchat" },
  { name: "Stackoverflow", slug: "stackoverflow" },
  { name: "Strava", slug: "strava" },
  { name: "Sublime Text", slug: "sublimetext" },
  { name: "Svelte", slug: "svelte" },
  { name: "Swift", slug: "swift" },
  { name: "Tailwind CSS", slug: "tailwindcss" },
  { name: "TensorFlow", slug: "tensorflow" },
  { name: "Tinder", slug: "tinder" },
  { name: "Tumblr", slug: "tumblr" },
  { name: "TypeScript", slug: "typescript" },
  { name: "Uber", slug: "uber" },
  { name: "Ubuntu", slug: "ubuntu" },
  { name: "Udemy", slug: "udemy" },
  { name: "Unity", slug: "unity" },
  { name: "Unsplash", slug: "unsplash" },
  { name: "Visual Studio", slug: "custom_visualstudio" },
  { name: "Visual Studio Code", slug: "custom_vscode", icon: "https://cdn.iconscout.com/icon/free/png-256/free-visual-studio-code-3251603-2724650.png" },
  { name: "Vue.js", slug: "vuedotjs" },
  { name: "Wix", slug: "wix" },
  { name: "Xbox", slug: "custom_xbox" },
  { name: "Xcode", slug: "xcode" },
  { name: "Yahoo", slug: "custom_yahoo", icon: "https://cdn.iconscout.com/icon/free/png-256/free-yahoo-14-1175110.png" },
  { name: "Yelp", slug: "yelp" }
];

interface TouchPointFieldProps {
  label: string;
  touchPoints: TouchPoint[];
  onChange: (touchPoints: TouchPoint[]) => void;
  placeholder?: string;
}

const TouchPointField: React.FC<TouchPointFieldProps> = ({
  label,
  touchPoints,
  onChange,
  placeholder = "Add touch points..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempTouchPoints, setTempTouchPoints] = useState<TouchPoint[]>(touchPoints || []);
  const [selectedTouchPoint, setSelectedTouchPoint] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [customBrandName, setCustomBrandName] = useState('');
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  const [customLogoFile, setCustomLogoFile] = useState<File | null>(null);
  const [customLogoPreview, setCustomLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempTouchPoints(touchPoints || []);
  }, [touchPoints]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingContainerRef.current && !editingContainerRef.current.contains(event.target as Node) && isEditing) {
        setIsEditing(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  const handleAddTouchPoint = (slugValue: string) => {
    // Find the selected app from the common apps list
    const selectedApp = COMMON_APPS.find(app => app.slug === slugValue);
    
    if (!selectedApp) return;
    
    // Check if this icon is already in use
    const isDuplicate = tempTouchPoints.some(tp => tp.slug === selectedApp.slug);
    if (isDuplicate) return;
    
    // For custom icons (like Microsoft products that were removed from SimpleIcons)
    const isCustomIcon = selectedApp.slug.startsWith('custom_') || !!selectedApp.icon;
    
    const newTouchPoint: TouchPoint = {
      id: `touchpoint-${Date.now()}`,
      icon: isCustomIcon ? selectedApp.icon : '',
      name: selectedApp.name,
      slug: selectedApp.slug,
      isCustom: isCustomIcon
    };
    
    const updatedTouchPoints = [...tempTouchPoints, newTouchPoint];
    setTempTouchPoints(updatedTouchPoints);
    onChange(updatedTouchPoints); // Auto-save when adding
    setSelectedTouchPoint(undefined); // Reset selection
    setSearchTerm(''); // Clear search after adding
  };

  const handleAddCustomTouchPoint = () => {
    if (!customBrandName.trim()) return;
    
    let logoData = '';
    
    // If a custom logo file was uploaded, use the preview URL
    if (customLogoPreview) {
      logoData = customLogoPreview;
    } else if (customLogoUrl) {
      // If a URL was provided, use that
      logoData = customLogoUrl;
    }
    
    const newCustomTouchPoint: TouchPoint = {
      id: `touchpoint-custom-${Date.now()}`,
      icon: logoData,
      name: customBrandName.trim(),
      isCustom: true
    };
    
    const updatedTouchPoints = [...tempTouchPoints, newCustomTouchPoint];
    setTempTouchPoints(updatedTouchPoints);
    onChange(updatedTouchPoints); // Auto-save when adding custom
    
    // Reset the custom brand form
    setCustomBrandName('');
    setCustomLogoUrl('');
    setCustomLogoFile(null);
    setCustomLogoPreview(null);
    setIsCustomDialogOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCustomLogoFile(file);
    
    // Create a preview URL for the image
    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    
    // Clear the URL input since we're using a file now
    setCustomLogoUrl('');
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomLogoUrl(e.target.value);
    
    // Clear the file input since we're using a URL now
    setCustomLogoFile(null);
    setCustomLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveTouchPoint = (id: string) => {
    const updatedTouchPoints = tempTouchPoints.filter(tp => tp.id !== id);
    setTempTouchPoints(updatedTouchPoints);
    onChange(updatedTouchPoints); // Auto-save when removing
  };

  // Filter apps by search term
  const filteredApps = COMMON_APPS
    .filter(app => app.slug !== "category_header") // Exclude category headers
    .filter(app => app.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(app => !tempTouchPoints.some(tp => tp.slug === app.slug));

  // Group apps by category
  const groupedApps = () => {
    const result: { [category: string]: typeof COMMON_APPS } = {};
    
    let currentCategory = "Others";
    
    COMMON_APPS.forEach(app => {
      if (app.slug === "category_header") {
        currentCategory = app.name.replace("//", "").trim();
        result[currentCategory] = [];
      } else if (filteredApps.some(fa => fa.slug === app.slug)) {
        if (!result[currentCategory]) {
          result[currentCategory] = [];
        }
        result[currentCategory].push(app);
      }
    });
    
    // Remove empty categories
    Object.keys(result).forEach(key => {
      if (result[key].length === 0) {
        delete result[key];
      }
    });
    
    return result;
  };

  if (isEditing) {
    return (
      <div className="space-y-2" ref={editingContainerRef}>
        <label className="text-[11px] font-medium block text-muted-foreground">
          {label}
        </label>
        
        <div className="space-y-2">
          {/* TouchPoint list */}
          <div className="flex flex-wrap gap-2">
            {tempTouchPoints.map((touchPoint) => (
              <TooltipProvider key={touchPoint.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      className="group bg-slate-50 dark:bg-slate-800 text-base w-8 h-8 rounded-full flex items-center justify-center relative shadow-sm hover:shadow transition-shadow"
                    >
                      {touchPoint.isCustom || touchPoint.icon ? (
                        <img 
                          src={touchPoint.icon as string} 
                          alt={touchPoint.name}
                          className="h-5 w-5 object-contain rounded-full"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            e.currentTarget.parentElement!.textContent = touchPoint.name.charAt(0);
                          }}
                        />
                      ) : touchPoint.slug && !touchPoint.slug.startsWith('custom_') ? (
                        <img 
                          className="h-5 w-5"
                          src={`https://cdn.simpleicons.org/${touchPoint.slug}`}
                          alt={touchPoint.name} 
                          onError={(e) => {
                            // If image fails to load, show the first letter of the name
                            (e.target as HTMLImageElement).style.display = 'none';
                            e.currentTarget.parentElement!.textContent = touchPoint.name.charAt(0);
                          }}
                        />
                      ) : (
                        <span className="text-sm">{touchPoint.name.charAt(0)}</span>
                      )}
                      <button
                        type="button"
                        className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-4 w-4 flex items-center justify-center"
                        onClick={() => handleRemoveTouchPoint(touchPoint.id)}
                      >
                        <X className="h-2 w-2" />
                      </button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{touchPoint.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
          
          {/* Search input for touch points */}
          <div className="relative">
            <Input
              placeholder="Search for apps..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
          
          {/* Add custom brand button */}
          <Dialog open={isCustomDialogOpen} onOpenChange={setIsCustomDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full flex items-center justify-center gap-1"
              >
                <PlusCircle className="h-3.5 w-3.5" />
                <span>Add Custom Brand</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Custom Brand</DialogTitle>
                <DialogDescription>
                  Add your own brand that isn't in our library. You can upload a logo or use a URL.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="brand-name">Brand Name</Label>
                  <Input 
                    id="brand-name" 
                    value={customBrandName} 
                    onChange={(e) => setCustomBrandName(e.target.value)}
                    placeholder="Enter brand name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 border-2 border-dashed rounded-md flex items-center justify-center overflow-hidden">
                      {customLogoPreview ? (
                        <img 
                          src={customLogoPreview} 
                          alt="Logo preview" 
                          className="h-full w-full object-contain"
                        />
                      ) : customLogoUrl ? (
                        <img 
                          src={customLogoUrl} 
                          alt="Logo preview" 
                          className="h-full w-full object-contain"
                          onError={() => setCustomLogoUrl('')}
                        />
                      ) : (
                        <Upload className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="text-xs"
                      />
                      <div className="text-xs text-muted-foreground text-center">or</div>
                      <Input
                        type="url"
                        placeholder="Enter logo URL"
                        value={customLogoUrl}
                        onChange={handleUrlChange}
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="ghost" onClick={() => setIsCustomDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleAddCustomTouchPoint}
                  disabled={!customBrandName.trim()}
                >
                  Add Brand
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Touch point selection */}
          <div className="max-h-60 overflow-y-auto border rounded-md">
            {filteredApps.length > 0 ? (
              <div className="divide-y">
                {Object.entries(groupedApps()).map(([category, apps]) => (
                  <div key={category} className="py-1">
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted/40">
                      {category}
                    </div>
                    <div className="py-1">
                      {apps.map(app => (
                        <Button
                          key={app.slug}
                          variant="ghost"
                          size="sm"
                          className="w-full h-8 justify-start px-2 text-sm rounded-none hover:bg-muted/50"
                          onClick={() => handleAddTouchPoint(app.slug)}
                        >
                          <div className="w-5 h-5 mr-3 flex items-center justify-center">
                            {app.slug.startsWith('custom_') || app.icon ? (
                              <img
                                className="h-4 w-4"
                                src={app.icon as string}
                                alt={app.name}
                                onError={(e) => {
                                  // If image fails to load, show the first letter of the name
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  e.currentTarget.parentElement!.textContent = app.name.charAt(0);
                                }}
                              />
                            ) : (
                              <img
                                className="h-4 w-4"
                                src={`https://cdn.simpleicons.org/${app.slug}`}
                                alt={app.name}
                                onError={(e) => {
                                  // If image fails to load, show the first letter of the name
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  e.currentTarget.parentElement!.textContent = app.name.charAt(0);
                                }}
                              />
                            )}
                          </div>
                          <span className="truncate">{app.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-2 text-xs text-center text-muted-foreground">
                {searchTerm ? "No matching apps found" : "All apps have been added"}
              </div>
            )}
            {searchTerm && filteredApps.length > 30 && (
              <div className="text-xs text-center text-muted-foreground p-1 border-t">
                {filteredApps.length - 30} more results. Refine your search.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {tempTouchPoints.length > 0 ? (
        <div 
          className="flex flex-wrap gap-2 min-h-[2.5rem] cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 p-2 rounded items-center"
          onClick={() => setIsEditing(true)}
        >
          {tempTouchPoints.map((touchPoint) => (
            <TooltipProvider key={touchPoint.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div 
                    className="group bg-slate-50 dark:bg-slate-800 text-base w-8 h-8 rounded-full flex items-center justify-center relative shadow-sm hover:shadow transition-shadow"
                  >
                    {touchPoint.isCustom || touchPoint.icon ? (
                      <img 
                        src={touchPoint.icon as string} 
                        alt={touchPoint.name}
                        className="h-5 w-5 object-contain rounded-full"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          e.currentTarget.parentElement!.textContent = touchPoint.name.charAt(0);
                        }}
                      />
                    ) : touchPoint.slug && !touchPoint.slug.startsWith('custom_') ? (
                      <img 
                        className="h-5 w-5"
                        src={`https://cdn.simpleicons.org/${touchPoint.slug}`}
                        alt={touchPoint.name} 
                        onError={(e) => {
                          // If image fails to load, show the first letter of the name
                          (e.target as HTMLImageElement).style.display = 'none';
                          e.currentTarget.parentElement!.textContent = touchPoint.name.charAt(0);
                        }}
                      />
                    ) : (
                      <span className="text-sm">{touchPoint.name.charAt(0)}</span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{touchPoint.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      ) : (
        <div 
          className="text-xs min-h-[2.5rem] cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 p-1 rounded text-muted-foreground italic flex items-center"
          onClick={() => setIsEditing(true)}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default TouchPointField; 